import React, { useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import { useDispatch } from "react-redux"
import CustomModal from "../../basics/interface/CustomModal"
import { COLORS } from "../../../utilities/Constants"
import {
	fetchMessageRendu,
	fetchPieceApercu,
	telechargerMessageFichier,
} from "../../../store/actions/assistancesActions"
import { dateCourte, ui } from "./assistancesUi"

// « Voir l'e-mail » (GE-57, visionneur 09.10) : l'e-mail tel qu'une
// messagerie l'affiche. Le HTML est NETTOYÉ côté serveur
// (assistances/rendu_email.py) puis affiché dans une iframe sandbox sans
// scripts ni même origine, avec sa propre CSP ; images distantes bloquées
// sauf demande. Pièces et .eml passent par l'API authentifiée (données
// clients : jamais par /media/assistances/).

// Liens du mail : nouvel onglet, hors sandbox. Rien d'autre n'est permis.
const SANDBOX = "allow-popups allow-popups-to-escape-sandbox"

const taille = (o) => {
	if (o === null || o === undefined) return ""
	if (o < 1024) return `${o} o`
	if (o < 1024 * 1024) return `${Math.round(o / 1024)} ko`
	return `${(o / 1024 / 1024).toFixed(1).replace(".", ",")} Mo`
}

const icone = (type) =>
	type === "application/pdf" ? "📄" : type.startsWith("image/") ? "🖼" : "📎"

const Squelette = () => (
	<div style={{ padding: "18px 24px" }}>
		{[70, 45, 90, 85, 60].map((w, i) => (
			<div
				key={`sq_${i}`}
				style={{
					height: i === 0 ? 16 : 11,
					width: `${w}%`,
					borderRadius: 6,
					backgroundColor: COLORS.bg,
					marginBottom: i === 1 ? 22 : 10,
				}}
			/>
		))}
	</div>
)

const EnTetes = ({ r }) => (
	<div style={st.entetes}>
		{[
			["De", r.entetes.de || r.expediteur],
			["À", r.entetes.a],
			["Cc", r.entetes.cc],
			["Date", r.entetes.date ? r.entetes.date : dateCourte(r.recu_le)],
		]
			.filter(([, v]) => v)
			.map(([libelle, v]) => (
				<React.Fragment key={libelle}>
					<span>{libelle}</span>
					<span style={st.valeur}>{v}</span>
				</React.Fragment>
			))}
	</div>
)

const estPdf = (pj) => pj.type === "application/pdf"

const Pieces = ({ pieces, ouvrir, telecharger }) => (
	<div style={st.pieces}>
		{pieces.map((pj) => (
			<span key={`pj_${pj.index}`} style={st.piece(pj.a_fichier)}>
				<button
					type="button"
					style={st.pieceNom}
					disabled={!pj.a_fichier}
					title={pj.apercu ? "Aperçu" : "Télécharger"}
					onClick={() =>
						pj.apercu ? ouvrir(pj.index) : telecharger(pj)
					}
				>
					<span role="img" aria-hidden="true">
						{icone(pj.type)}
					</span>
					<span style={st.ellipse}>{pj.nom}</span>
					<span style={st.taille}>
						{pj.a_fichier ? taille(pj.taille) : "archivée"}
					</span>
				</button>
				{pj.a_fichier && pj.apercu && (
					<button
						type="button"
						style={st.pieceDl}
						title="Télécharger"
						aria-label={`Télécharger ${pj.nom}`}
						onClick={() => telecharger(pj)}
					>
						↓
					</button>
				)}
			</span>
		))}
	</div>
)

// Aperçu plein écran d'une pièce de la liste blanche (PDF, image), ←/→
// entre les pièces prévisualisables.
const Apercu = ({ messageId, pieces, index, setIndex, onClose }) => {
	const dispatch = useDispatch()
	const [url, setUrl] = useState(null)
	const pj = pieces.find((p) => p.index === index)
	const liste = pieces.filter((p) => p.apercu && !estPdf(p))
	const pos = liste.findIndex((p) => p.index === index)
	useEffect(() => {
		let actif = true
		let cree = null
		setUrl(null)
		dispatch(fetchPieceApercu(messageId, index)).then((u) => {
			cree = u
			if (actif) setUrl(u || false)
			else if (u) window.URL.revokeObjectURL(u)
		})
		return () => {
			actif = false
			if (cree) window.URL.revokeObjectURL(cree)
		}
	}, [dispatch, messageId, index])
	const aller = (d) => {
		if (liste.length < 2) return
		setIndex(liste[(pos + d + liste.length) % liste.length].index)
	}
	useEffect(() => {
		const touche = (e) => {
			if (e.key === "ArrowLeft") aller(-1)
			if (e.key === "ArrowRight") aller(1)
		}
		window.addEventListener("keydown", touche)
		return () => window.removeEventListener("keydown", touche)
	})
	if (!pj) return null
	return ReactDOM.createPortal(
		<div style={st.apercuFond} data-assistance-ui="">
			<div style={st.apercuBarre}>
				<span style={{ ...st.ellipse, fontWeight: 600 }}>{pj.nom}</span>
				<span style={{ opacity: 0.7, fontSize: 12.5 }}>
					{taille(pj.taille)}
					{/* Un PDF en repli (popup bloquée) n'est pas dans la liste. */}
					{liste.length > 1 && pos >= 0
						? ` · ${pos + 1} / ${liste.length}`
						: ""}
				</span>
				<span style={{ flex: 1 }} />
				{liste.length > 1 && (
					<>
						<button
							type="button"
							style={st.apercuBtn}
							onClick={() => aller(-1)}
							aria-label="Pièce précédente"
						>
							←
						</button>
						<button
							type="button"
							style={st.apercuBtn}
							onClick={() => aller(1)}
							aria-label="Pièce suivante"
						>
							→
						</button>
					</>
				)}
				<button
					type="button"
					style={st.apercuBtn}
					onClick={() =>
						dispatch(
							telechargerMessageFichier(
								messageId,
								pj.index,
								pj.nom,
							),
						)
					}
				>
					Télécharger
				</button>
				<button
					type="button"
					style={st.apercuBtn}
					onClick={onClose}
					aria-label="Fermer l'aperçu"
				>
					×
				</button>
			</div>
			<div style={st.apercuCorps}>
				{url === null && (
					<span style={{ opacity: 0.7 }}>Chargement…</span>
				)}
				{url === false && (
					<span style={{ opacity: 0.7 }}>Aperçu indisponible.</span>
				)}
				{url && pj.type === "application/pdf" && (
					<iframe title={pj.nom} src={url} style={st.apercuPdf} />
				)}
				{url && pj.type.startsWith("image/") && (
					<img src={url} alt={pj.nom} style={st.apercuImage} />
				)}
			</div>
		</div>,
		document.body,
	)
}

const MessageModal = ({ messageId, onClose }) => {
	const dispatch = useDispatch()
	const [r, setR] = useState(null)
	const [images, setImages] = useState(false)
	const [apercu, setApercu] = useState(null)
	const monte = useRef(true)
	useEffect(
		() => () => {
			monte.current = false
		},
		[],
	)
	useEffect(() => {
		dispatch(fetchMessageRendu(messageId, images)).then((data) => {
			if (monte.current) setR(data && !data.erreur ? data : false)
		})
	}, [dispatch, messageId, images])
	// Échap : ferme l'aperçu d'abord, puis le visionneur.
	const apercuRef = useRef(apercu)
	apercuRef.current = apercu
	useEffect(() => {
		const touche = (e) => {
			if (e.key !== "Escape") return
			if (apercuRef.current !== null) setApercu(null)
			else onClose()
		}
		window.addEventListener("keydown", touche)
		return () => window.removeEventListener("keydown", touche)
	}, [onClose])

	const telecharger = (pj) =>
		dispatch(telechargerMessageFichier(messageId, pj.index, pj.nom))
	// PDF : nouvel onglet (demande Jordan 09.10). L'onglet est ouvert dans le
	// clic (sinon bloqué comme popup) puis reçoit le blob une fois chargé ;
	// popup bloquée → aperçu dans l'app, comme avant.
	const ouvrirPiece = (index) => {
		const pj = r.pieces.find((p) => p.index === index)
		if (!pj || !estPdf(pj)) return setApercu(index)
		const onglet = window.open("", "_blank")
		if (!onglet) return setApercu(index)
		onglet.document.title = pj.nom
		dispatch(fetchPieceApercu(messageId, index)).then((url) => {
			if (!url) return onglet.close()
			onglet.location.href = url
			// Blob gardé tant que l'onglet est ouvert (recharger, télécharger
			// depuis le lecteur), révoqué à sa fermeture.
			const veille = setInterval(() => {
				if (!onglet.closed) return
				clearInterval(veille)
				window.URL.revokeObjectURL(url)
			}, 5000)
		})
	}
	const refs = r
		? [r.assistance, r.role, r.ref_affaire, r.ref_ordre]
				.filter(Boolean)
				.join(" · ")
		: ""

	// Portail vers <body> : ouverte depuis une fiche en lecture seule, la
	// fenêtre serait sinon dans le <fieldset disabled> (boutons inertes,
	// contre-revue GE-60 R2).
	return ReactDOM.createPortal(
		<div data-modal-assistance="" data-assistance-ui="">
			<CustomModal
				title={
					r
						? r.entetes.sujet || r.sujet || "(sans objet)"
						: "E-mail reçu"
				}
				subtitle={refs || null}
				onClose={onClose}
				maxWidth={880}
				bodyStyle={st.body}
				buttons={[
					{
						title: "Fermer",
						styleName: "secondary",
						onClick: onClose,
					},
				]}
				footerLeft={
					r && !r.archive ? (
						<button
							type="button"
							style={ui.lien}
							onClick={() =>
								dispatch(telechargerMessageFichier(messageId))
							}
						>
							Télécharger l'e-mail (.eml)
						</button>
					) : null
				}
			>
				{r === null && <Squelette />}
				{r === false && (
					<div
						style={{
							padding: "18px 24px",
							color: COLORS.textMuted,
						}}
					>
						Message introuvable.
					</div>
				)}
				{r && (
					<>
						<EnTetes r={r} />
						{r.archive && (
							<div style={st.bandeau}>
								{r.archive_le
									? `E-mail archivé le ${r.archive_le
											.split("-")
											.reverse()
											.join(
												".",
											)} — l'original est dans la boîte mail.`
									: "E-mail original archivé : seul le texte lu à la réception est affiché."}
							</div>
						)}
						{r.avertissement && (
							<div style={st.bandeau}>{r.avertissement}</div>
						)}
						{r.images_distantes > 0 && (
							<div style={st.bandeau}>
								{r.images_distantes === 1
									? "1 image distante bloquée"
									: `${r.images_distantes} images distantes bloquées`}{" "}
								(protection contre le suivi).
								<button
									type="button"
									style={{ ...ui.lien, marginLeft: 8 }}
									onClick={() => setImages(true)}
								>
									Afficher les images
								</button>
							</div>
						)}
						<iframe
							title="Contenu de l'e-mail"
							sandbox={SANDBOX}
							srcDoc={r.document}
							style={st.iframe}
						/>
						{r.pieces.length > 0 && (
							<Pieces
								pieces={r.pieces}
								ouvrir={ouvrirPiece}
								telecharger={telecharger}
							/>
						)}
					</>
				)}
			</CustomModal>
			{r && apercu !== null && (
				<Apercu
					messageId={messageId}
					pieces={r.pieces}
					index={apercu}
					setIndex={setApercu}
					onClose={() => setApercu(null)}
				/>
			)}
		</div>,
		document.body,
	)
}

const st = {
	body: {
		padding: 0,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
	},
	entetes: {
		fontSize: 12.5,
		color: COLORS.textMuted,
		display: "grid",
		gridTemplateColumns: "44px minmax(0, 1fr)",
		gap: "3px 10px",
		padding: "14px 24px",
		borderBottom: `1px solid ${COLORS.border}`,
	},
	valeur: { color: COLORS.text, overflowWrap: "anywhere" },
	bandeau: {
		fontSize: 12.5,
		padding: "8px 24px",
		backgroundColor: COLORS.warningLight,
		color: "#92400E",
		borderBottom: "1px solid #FDE68A",
	},
	iframe: {
		border: "none",
		width: "100%",
		height: "min(58vh, 620px)",
		minHeight: 260,
		display: "block",
		backgroundColor: "#fff",
	},
	pieces: {
		display: "flex",
		flexWrap: "wrap",
		gap: 8,
		padding: "12px 24px",
		borderTop: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.bg,
	},
	piece: (actif) => ({
		display: "inline-flex",
		alignItems: "stretch",
		border: `1px solid ${COLORS.border}`,
		borderRadius: 8,
		backgroundColor: COLORS.surface,
		opacity: actif ? 1 : 0.55,
		maxWidth: "100%",
		overflow: "hidden",
	}),
	pieceNom: {
		display: "inline-flex",
		alignItems: "center",
		gap: 7,
		fontSize: 12.5,
		padding: "6px 10px",
		border: "none",
		background: "none",
		color: COLORS.text,
		cursor: "pointer",
		fontFamily: "inherit",
		minWidth: 0,
	},
	pieceDl: {
		border: "none",
		borderLeft: `1px solid ${COLORS.border}`,
		background: "none",
		padding: "0 10px",
		cursor: "pointer",
		color: COLORS.textMuted,
		fontFamily: "inherit",
	},
	ellipse: {
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		maxWidth: 260,
	},
	taille: { color: COLORS.textMuted, fontSize: 11.5, whiteSpace: "nowrap" },
	apercuFond: {
		position: "fixed",
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		zIndex: 3000,
		backgroundColor: "rgba(28, 25, 23, 0.92)",
		display: "flex",
		flexDirection: "column",
		color: "#fff",
	},
	apercuBarre: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "10px 16px",
		minWidth: 0,
	},
	apercuBtn: {
		border: "1px solid rgba(255,255,255,0.3)",
		background: "none",
		color: "#fff",
		borderRadius: 8,
		padding: "5px 11px",
		cursor: "pointer",
		fontFamily: "inherit",
		fontSize: 13,
	},
	apercuCorps: {
		flex: 1,
		minHeight: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0 16px 16px",
	},
	apercuPdf: {
		width: "100%",
		height: "100%",
		border: "none",
		borderRadius: 8,
		backgroundColor: "#fff",
	},
	apercuImage: { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" },
}

export default MessageModal
